import { useEffect } from "react";
import { ScanLine } from "lucide-react";

/**
 * Inline scanner overlay — fills its RELATIVE parent (not fixed / not portal).
 * Renders inside the image-preview dropzone so the animation stays "inside
 * the frame". Parent must have `position: relative`.
 */
export default function ExtractionOverlay({ open, phaseSpeed = 900 }) {
  useEffect(() => {}, [open]);
  if (!open) return null;

  return (
    <div
      className="absolute inset-0 z-30 rounded-lg overflow-hidden"
      role="status"
      aria-busy="true"
      data-testid="extraction-overlay"
      style={{
        backgroundColor: "color-mix(in oklab, var(--bg) 55%, transparent)",
        backdropFilter: "blur(6px)",
      }}
    >
      {/* Corner brackets */}
      <div className="corner-brackets absolute inset-0 z-30 pointer-events-none">
        <span /><span /><span /><span />
      </div>

      {/* Grid overlay drifting */}
      <div className="absolute inset-0" style={{
        backgroundImage:
          "linear-gradient(rgba(0,230,118,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(0,230,118,0.16) 1px, transparent 1px)",
        backgroundSize: "20px 20px",
        animation: "grid-drift 3s linear infinite",
        mixBlendMode: "screen",
      }} />

      {/* Vertical scan bar */}
      <div
        className="absolute left-0 right-0 h-[45%] pointer-events-none"
        style={{
          top: 0,
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(0,230,118,0.08) 40%, rgba(0,230,118,0.7) 50%, rgba(0,230,118,0.08) 60%, transparent 100%)",
          filter: "blur(1px)",
          animation: "scanline 2s cubic-bezier(0.22, 1, 0.36, 1) infinite",
          mixBlendMode: "screen",
        }}
      />

      {/* Compact radar SVG in the center */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <svg
          viewBox="0 0 240 240"
          className="w-1/2 h-1/2 max-w-[200px]"
          style={{ filter: "drop-shadow(0 0 24px rgba(0,230,118,0.35))" }}
        >
          <defs>
            <linearGradient id="ov-sweep" x1="120" y1="120" x2="240" y2="120" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="var(--invest)" stopOpacity="0.85" />
              <stop offset="100%" stopColor="var(--invest)" stopOpacity="0" />
            </linearGradient>
            <radialGradient id="ov-core" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="var(--invest)" stopOpacity="0.8" />
              <stop offset="60%" stopColor="var(--invest)" stopOpacity="0.05" />
              <stop offset="100%" stopColor="var(--invest)" stopOpacity="0" />
            </radialGradient>
          </defs>

          <g stroke="var(--invest)" strokeOpacity="0.35" fill="none">
            <circle cx="120" cy="120" r="40" strokeDasharray="2 4" />
            <circle cx="120" cy="120" r="70" strokeDasharray="2 6" strokeOpacity="0.22" />
            <circle cx="120" cy="120" r="100" strokeDasharray="2 8" strokeOpacity="0.15" />
          </g>

          <g stroke="var(--invest)" strokeOpacity="0.4" strokeWidth="0.6">
            <line x1="0" y1="120" x2="240" y2="120" strokeDasharray="1 6" />
            <line x1="120" y1="0" x2="120" y2="240" strokeDasharray="1 6" />
          </g>

          <circle cx="120" cy="120" r="110" fill="url(#ov-core)" />

          <g style={{ transformOrigin: "120px 120px", animation: "rotate-slow 2.4s linear infinite" }}>
            <path d="M120 120 L240 120 A120 120 0 0 0 200 40 Z" fill="url(#ov-sweep)" />
          </g>

          <g style={{ transformOrigin: "120px 120px", animation: "rotate-slow-rev 8s linear infinite" }}>
            <circle cx="120" cy="120" r="112" fill="none" stroke="var(--invest)" strokeOpacity="0.55" strokeWidth="0.8" strokeDasharray="6 3" />
          </g>

          <circle cx="120" cy="120" r="4" fill="var(--invest)">
            <animate attributeName="r" values="2.5;6;2.5" dur="1.2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0.4;1" dur="1.2s" repeatCount="indefinite" />
          </circle>
        </svg>
      </div>

      {/* Bottom label */}
      <div className="absolute inset-x-0 bottom-3 z-20 flex flex-col items-center pointer-events-none">
        <div className="shimmer-text font-display text-[11px] uppercase tracking-[0.35em]" data-testid="extraction-overlay-title">
          Analyse par l'IA
        </div>
        <div className="mt-1.5 w-24 progress-bar" />
        <div className="mt-1.5 flex items-center gap-1 font-mono-num text-[9px] text-muted-1">
          <ScanLine size={9} className="text-invest" />
          <span>Gemini · vision multimodale</span>
        </div>
      </div>
    </div>
  );
}
